import $dictionary from 'app/stores/$dictionary';
import $metrics from 'app/stores/metrics/$metrics';
import { SUPPRESSION_TYPES } from '@kentik/ui-shared/alerting/constants';
import type { SuppressionFormFilter } from './SuppressionModel';

export interface SuppressionDimension {
  label: string;
  group?: string;
}

export interface SuppressionDimensionContext {
  type: string;
  measurement?: string;
}

interface DimensionEntry {
  label?: string;
  Label?: string;
  name?: string;
  group?: string;
}

const IMPLICIT_METRIC_FIELDS = ['km_measurement_name', 'km_device_id'];

const IMPLICIT_METRIC_LABELS: Record<string, string> = {
  km_measurement_name: 'Measurement',
  km_device_id: 'Device ID'
};

const NMS_DEVICE_FIELD = 'device_id';

// Carried by kmetrics suppressions themselves, so they are not conditions the user configured.
export const isImplicitMetricField = (field = ''): boolean =>
  IMPLICIT_METRIC_FIELDS.some((name) => field.endsWith(name));

export const unknownDimensionLabel = (key: string): string => `Unknown dimension: ${key}`;

const toDimension = (entry: DimensionEntry | undefined, fallbackLabel?: string): SuppressionDimension | undefined => {
  const label = entry?.label || entry?.Label || entry?.name || fallbackLabel;

  return entry && label ? { label, group: entry.group } : undefined;
};

const dictionaryDimension = (key: string) =>
  toDimension(($dictionary.dimensionLabelsMap as Record<string, DimensionEntry> | undefined)?.[key]);

const nmsDimension = (key: string, measurement?: string) => {
  if (key === NMS_DEVICE_FIELD) {
    return { label: 'Device ID' };
  }

  const dimensions = measurement ? $metrics.measurementModel(measurement)?.get('storage.Dimensions') : undefined;

  return toDimension((dimensions as Record<string, DimensionEntry> | undefined)?.[key], key);
};

const metricDimension = (key: string, measurement?: string) => {
  const implicitField = IMPLICIT_METRIC_FIELDS.find((name) => key.endsWith(name));

  if (implicitField) {
    return { label: IMPLICIT_METRIC_LABELS[implicitField] };
  }

  return (
    toDimension(measurement ? $metrics.getDimensionForMeasurement(measurement, key) : undefined) ||
    dictionaryDimension(key)
  );
};

export function resolveSuppressionDimension(
  key: string,
  { type, measurement }: SuppressionDimensionContext
): SuppressionDimension | undefined {
  if (type === SUPPRESSION_TYPES.NMS) {
    return nmsDimension(key, measurement);
  }

  if (type === SUPPRESSION_TYPES.METRIC) {
    return metricDimension(key, measurement);
  }

  return dictionaryDimension(key);
}

const CHECKED_TYPES: string[] = [SUPPRESSION_TYPES.CONDITION, SUPPRESSION_TYPES.METRIC, SUPPRESSION_TYPES.NMS];

const canJudgeDimensions = ({ type, measurement }: SuppressionDimensionContext) => {
  if (type === SUPPRESSION_TYPES.CONDITION) {
    return Boolean($dictionary.dimensionLabelsMap);
  }

  const { hasFetched, requestStatus } = $metrics.availableMetricsCollection;

  return Boolean(
    $dictionary.dimensionLabelsMap &&
    hasFetched &&
    !requestStatus &&
    measurement &&
    $metrics.measurementModel(measurement)
  );
};

export function getUnknownDimensions(keys: string[], context: SuppressionDimensionContext): string[] {
  if (!CHECKED_TYPES.includes(context.type) || !canJudgeDimensions(context)) {
    return [];
  }

  return [...new Set(keys.filter((key) => key && !resolveSuppressionDimension(key, context)))];
}

interface SuppressionDimensionFormValues {
  type?: string;
  measurement?: string;
  conditionsGroup?: Array<{ filters?: SuppressionFormFilter[] }>;
}

export function getUnknownFormDimensions({ type, measurement, conditionsGroup = [] }: SuppressionDimensionFormValues) {
  const filters = conditionsGroup.flatMap((group) => group.filters || []);

  if (!type) {
    return [];
  }

  if (type !== SUPPRESSION_TYPES.METRIC) {
    return getUnknownDimensions(
      filters.map(({ filterField }) => filterField),
      { type, measurement }
    );
  }

  const metricMeasurement = filters.find(({ filterField }) =>
    filterField?.endsWith('km_measurement_name')
  )?.filterValue;
  const measurementName = typeof metricMeasurement === 'string' ? metricMeasurement : undefined;
  const storedFilters: SuppressionFormFilter[] = $metrics.dimensionFilterReconToKdeTransformer(
    filters,
    measurementName
  );
  const unknownStored = getUnknownDimensions(
    storedFilters.map(({ filterField }) => filterField),
    { type, measurement: measurementName }
  );

  return filters
    .filter((_filter, index) => unknownStored.includes(storedFilters[index].filterField))
    .map(({ filterField }) => filterField);
}
